/**
 * Shared formatting helpers and style constants for NH Room Dashboard pages.
 * Used by: nh-room-dashboard components, nh-session-history, nh-session-detail.
 *
 * All color tokens are derived from the MUI theme to support both light and dark mode,
 * matching the main dashboard (/dashboard) design system exactly.
 */

import { useTheme } from '@mui/material';

// ─── Accent Constant ─────────────────────────────────────────────────────────

export const ACCENT_COLOR = '#e2001b';

// ─── Theme-Aware Token Hook ──────────────────────────────────────────────────

/**
 * Returns all dashboard design-system tokens for the current theme mode.
 * Mirrors the exact color tokens used by /dashboard (layouts/dashboard/index.js).
 */
export function useDashboardTheme() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return {
    theme,
    isDark,
    divider: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)',
    panelHeaderBg: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa',
    tableHeaderBg: isDark ? '#1d2b47' : '#f4f5f7',
    tableHeaderColor: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
    primaryText: isDark ? theme.palette.text.primary : '#0f172a',
    secondaryText: isDark ? theme.palette.text.secondary : '#6b7280',
    rowAlt: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
    rowTop: isDark ? 'rgba(226,0,27,0.06)' : 'rgba(226,0,27,0.025)',
    rowHover: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
    rowBorder: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
    barTrack: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)',
    footerBg: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
    footerText: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.35)',
    rankTopColor: ACCENT_COLOR,
    rankColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)',
    titleColor: isDark ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)',
    chartLabelColor: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.65)',
    chartGridColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)'
  };
}

// ─── Theme-Aware Table Styles ────────────────────────────────────────────────

/** Table header <th> style via '& th' — matches main dashboard exactly */
export const tableHeadRowSx = (t) => ({
  '& th': {
    background: t.tableHeaderBg,
    color: t.tableHeaderColor,
    fontWeight: 700,
    fontSize: '0.67rem',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    padding: { xs: '9px 10px', sm: '9px 16px' },
    borderBottom: `1px solid ${t.divider}`,
    whiteSpace: 'nowrap',
    boxShadow: 'none'
  }
});

/** Body <TableRow> sx — handles alternating bg, top-row highlight, hover, border */
export const tableRowSx = (t, { index = 0, isTop = false } = {}) => ({
  background: isTop ? t.rowTop : index % 2 === 0 ? 'transparent' : t.rowAlt,
  '@media (hover: hover)': { '&:hover': { background: t.rowHover } },
  '& td': {
    padding: { xs: '9px 10px', sm: '9px 16px' },
    borderBottom: `1px solid ${t.rowBorder}`
  },
  '&:last-child td': { borderBottom: 'none' }
});

/** Scrollbar styling for TableContainer */
export const scrollbarSx = (t) => ({
  maxHeight: 460,
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': { height: '4px', width: '4px' },
  '&::-webkit-scrollbar-thumb': {
    background: t.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
    borderRadius: '2px'
  }
});

/** Panel header sx (for Card headers above tables/content) */
export const panelHeaderSx = (t) => ({
  px: { xs: 2, sm: 3 },
  py: 1.75,
  borderBottom: `1px solid ${t.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: t.panelHeaderBg
});

/** Count badge sx (e.g. "14 players", "9 blocks") — sits inside panelHeaderSx */
export const countBadgeSx = (t) => ({
  display: 'inline-flex',
  alignItems: 'center',
  px: 1.25,
  py: 0.35,
  borderRadius: '6px',
  lineHeight: 1,
  background: t.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
  border: `1px solid ${t.divider}`
});

/** Card wrapper sx */
export const cardSx = {
  borderRadius: '14px',
  overflow: 'hidden',
  position: 'relative'
};

/** Footer bar sx */
export const footerBarSx = (t) => ({
  px: { xs: 2, sm: 3 },
  py: 1.25,
  borderTop: `1px solid ${t.divider}`,
  background: t.footerBg
});

// ─── Action Button ──────────────────────────────────────────────────────────

/**
 * Theme-aware outlined action button sx.
 * Use on MDBox component='button' for small toolbar-style actions (export, share, etc.).
 *
 * @param {object} t - useDashboardTheme() return value
 * @param {object} [opts]
 * @param {string} [opts.accent] - override accent color (default: theme-based neutral)
 * @param {boolean} [opts.disabled] - disabled state
 */
export const actionButtonSx = (t, { accent, disabled } = {}) => {
  const c = accent || (t.isDark ? 'rgba(255,255,255,0.7)' : '#344767');
  return {
    display: 'inline-flex', alignItems: 'center', gap: 0.4,
    px: 1.2, py: 0.5, borderRadius: '8px',
    cursor: disabled ? 'default' : 'pointer',
    border: `1px solid ${c}`, background: 'transparent',
    color: c, fontSize: '0.68rem', fontWeight: 600, fontFamily: 'inherit',
    opacity: disabled ? 0.45 : 1,
    transition: 'all 0.15s ease',
    '&:hover': disabled ? {} : {
      background: t.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
      borderColor: t.isDark ? '#fff' : '#1a1a2e',
      color: t.isDark ? '#fff' : '#1a1a2e',
    },
  };
};

// ─── Legacy exports (kept for backward compat, but prefer theme-aware versions) ─

export const cellSx = {
  color: '#cdd5e0',
  borderColor: 'rgba(255,255,255,0.06)',
  py: 0.8,
  px: 1,
  fontSize: '0.78rem'
};

export const headSx = {
  ...cellSx,
  color: '#8899aa',
  fontWeight: 600,
  fontSize: '0.7rem',
  textTransform: 'uppercase'
};

// ─── Player P&L ─────────────────────────────────────────────────────────────

/** Per-player P&L: earnings minus normal bids minus winning boss bids. */
export const computePnl = (p) => (p.totalEarningsUsd || 0) - (p.normalBidTotal || 0) - (p.bossBidSpentUsd || 0);

// ─── Number Formatters ───────────────────────────────────────────────────────

export const fmt = (v, decimals = 2) => {
  if (v == null || isNaN(v)) return '—';
  return Number(v).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

export const fmtBtc = (v) => fmt(v, 8);

// ─── Date/Time Formatters ────────────────────────────────────────────────────

export const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium' });
};

export const fmtDateShort = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

export const fmtTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export const fmtTimeMedium = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export const fmtTimeShort = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export const fmtTimeFromEpoch = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

// ─── Misc Helpers ────────────────────────────────────────────────────────────

export const truncHash = (h) => {
  if (!h) return '—';
  return `${h.slice(0, 8)}...${h.slice(-6)}`;
};

/** Map raw engine status enum → user-friendly label + chip color */
export const engineStatusMap = {
  ACTIVE:   { label: 'Running',     color: 'success' },
  EXPIRED:  { label: 'Closed',      color: 'warning' },
  ERROR:    { label: 'Error',       color: 'error' },
  INACTIVE: { label: 'Not Started', color: 'default' }
};

/** Map event status → chip color */
export const eventStatusColor = {
  live:     'success',
  ended:    'error',
  disabled: 'warning'
};

/**
 * Derive a human-readable event label from eventStatus.
 * When liveState is absent, the session has ended.
 */
export const deriveEventLabel = (eventStatus) => {
  if (eventStatus === 'live') return 'Live';
  if (eventStatus === 'ended') return 'Ended';
  if (eventStatus === 'disabled') return 'Disabled';
  return eventStatus ? eventStatus.charAt(0).toUpperCase() + eventStatus.slice(1) : 'Ended';
};

/** Solid accent bar style for card headers — matches main dashboard */
export const accentBarSx = {
  height: '3px',
  background: ACCENT_COLOR
};

/** @deprecated Use accentBarSx instead */
export const gradientBarSx = accentBarSx;
